import type { DateTime } from '../../../schema/frameworks/entry.js';
import type { EventStatus, SportType } from '../../../schema/sport/entry.js';

const statusTimeout: Partial<Record<SportType, number>> = {
  Trotting: 60 * 60 * 1000,
  SkiingCrossCountry: 60 * 60 * 12 * 1000,
  Biathlon: 60 * 60 * 12 * 1000,
};

/**
 * Checks if the matchStart time has been surpassed based on sport type specific timeouts.
 *
 * @param sportType sport type for event
 * @param matchStart match start time
 * @param sportEventStatus current status of the sport event
 * @returns boolean indicating if the match start time has been surpassed
 */
export function hasMatchStartSurpassed(
  sportType: SportType,
  matchStart: DateTime,
  sportEventStatus: EventStatus
): boolean {
  const now = new Date();
  const then = new Date(matchStart);
  return Boolean(
    statusTimeout[sportType] &&
    now.getTime() - then.getTime() > statusTimeout[sportType] &&
    sportEventStatus === 'NotStarted'
  );
}

/* Sportinfo matchstates */
type SportinfoMatchState = 'NotStarted' | 'Ongoing' | 'Finished';

/* eslint complexity: ["error", {"max": 118}] */

/**
 * Returns a matchState based on sporteventStatus.
 *
 * @param sportEventStatus status of event to check for
 * @returns string
 */
export function getSportInfoMatchState(sportEventStatus: EventStatus): SportinfoMatchState {
  switch (sportEventStatus) {
    case 'Undefined':
    case 'NotStarted':
    case 'AboutToStart':
    case 'Delayed':
    case 'Postponed':
      return 'NotStarted';
    case 'Live':
    case 'FirstPeriod':
    case 'SecondPeriod':
    case 'ThirdPeriod':
    case 'FirstHalf':
    case 'SecondHalf':
    case 'FirstQuarter':
    case 'SecondQuarter':
    case 'ThirdQuarter':
    case 'FourthQuarter':
    case 'FirstInning':
    case 'SecondInning':
    case 'ThirdInning':
    case 'FourthInning':
    case 'FifthInning':
    case 'SixthInning':
    case 'SeventhInning':
    case 'EighthInning':
    case 'NinthInning':
    case 'Overtime':
    case 'FirstExtraPeriod':
    case 'SecondExtraPeriod':
    case 'ThirdExtraPeriod':
    case 'AwaitingPenalties':
    case 'Penalties':
    case 'Pause':
    case 'AwaitingExtratime':
    case 'Halftime':
    case 'Extratime':
    case 'Interrupted':
    case 'FallbackFourthPeriod':
    case 'FallbackFifthPeriod':
    case 'FallbackFirstSet':
    case 'FallbackSecondSet':
    case 'FallbackThirdSet':
    case 'FallbackFourthSet':
    case 'FallbackFifthSet':
    case 'FallbackExtraTimeHalftime':
    case 'FallbackInFirstBreak':
    case 'FallbackInSecondBreak':
    case 'FallbackInThirdBreak':
    case 'FallbackGoldenSet':
    case 'FallbackSixthSet':
    case 'FallbackSeventhSet':
    case 'FallbackFrame1':
    case 'FallbackFrame2':
    case 'FallbackFrame3':
    case 'FallbackFrame4':
    case 'FallbackFrame5':
    case 'FallbackFrame6':
    case 'FallbackFrame7':
    case 'FallbackFrame8':
    case 'FallbackFrame9':
    case 'FallbackFrame10':
    case 'FallbackFrame11':
    case 'FallbackFrame12':
    case 'FallbackFrame13':
    case 'FallbackFrame14':
    case 'FallbackFrame15':
    case 'FallbackFrame16':
    case 'FallbackFrame17':
    case 'FallbackFrame18':
    case 'FallbackFrame19':
    case 'FallbackFrame20':
    case 'FallbackFrame21':
    case 'FallbackFrame22':
    case 'FallbackFrame23':
    case 'FallbackFrame24':
    case 'FallbackFrame25':
    case 'FallbackFrame26':
    case 'FallbackFrame27':
    case 'FallbackFrame28':
    case 'FallbackFrame29':
    case 'FallbackFrame30':
    case 'FallbackFrame31':
    case 'FallbackFrame32':
    case 'FallbackFrame33':
    case 'FallbackFrame34':
    case 'FallbackFrame35':
    case 'FallbackFrame36':
    case 'FallbackFrame37':
    case 'FallbackAwaitingSuddenDeath':
    case 'FallbackInSuddenDeath':
    case 'FallbackFirstInningsHomeTeam':
    case 'FallbackFirstInningsAwayTeam':
    case 'FallbackSecondInningsHomeTeam':
    case 'FallbackSecondInningsAwayTeam':
    case 'FallbackAwaitingSuperOver':
    case 'FallbackInSuperOverHomeTeam':
    case 'FallbackInSuperOverAwayTeam':
    case 'FallbackLunchBreak':
    case 'FallbackTeaBreak':
    case 'FallbackStumps':
    case 'FallbackSuspended':
      return 'Ongoing';
    case 'Abandoned':
    case 'Cancelled':
    case 'Ended':
    case 'Closed':
    case 'Fulltime':
    case 'EndedAfterExtratime':
    case 'EndedAfterPenalties':
    case 'FallbackWalkover':
    case 'FallbackParticipantRetired':
    case 'FallbackEndedAfterSuddenDeath':
    case 'FallbackEndedAfterSuperOver':
    case 'FallbackDefaulted':
    case 'FallbackWalkoverHomeTeamWon':
    case 'FallbackWalkoverAwayTeamWon':
    case 'FallbackParticipantRetiredHomeTeamWon':
    case 'FallbackParticipantRetiredAwayTeamWon':
    case 'FallbackDefaultedHomeTeamWon':
    case 'FallbackDefaultedAwayTeamWon':
      return 'Finished';
    default:
      return 'NotStarted';
  }
}

/**
 * Functions to get match event is not started.
 *
 * @param sportEventStatus status of event to check for
 * @returns boolean indicating if event is not started
 */
export function getIsNotStarted(sportEventStatus: EventStatus): boolean {
  return getSportInfoMatchState(sportEventStatus) === 'NotStarted';
}

/**
 * Functions to get match event is ongoing.
 *
 * @param sportEventStatus status of event to check for
 * @returns boolean indicating if event is ongoing
 */
export function getIsOngoing(sportEventStatus: EventStatus): boolean {
  return getSportInfoMatchState(sportEventStatus) === 'Ongoing';
}

/**
 * Functions to get match event is finished.
 *
 * @param sportEventStatus status of event to check for
 * @returns boolean indicating if event is finished
 */
export function getIsFinished(sportEventStatus: EventStatus): boolean {
  return getSportInfoMatchState(sportEventStatus) === 'Finished';
}

/**
 * Get is event in overtime.
 *
 * @param sportEventStatus status of event to check for
 * @returns boolean indicating if event is in overtime
 */
export function getIsOvertime(sportEventStatus: EventStatus): boolean {
  switch (sportEventStatus) {
    case 'Overtime':
    case 'FirstExtraPeriod':
    case 'SecondExtraPeriod':
    case 'ThirdExtraPeriod':
    case 'AwaitingPenalties':
    case 'Penalties':
    case 'AwaitingExtratime':
    case 'Extratime':
    case 'FallbackExtraTimeHalftime':
    case 'FallbackAwaitingSuddenDeath':
    case 'FallbackInSuddenDeath':
    case 'FallbackAwaitingSuperOver':
    case 'FallbackInSuperOverHomeTeam':
    case 'FallbackInSuperOverAwayTeam':
      return true;
    default:
      return false;
  }
}

/**
 * Get is event finished after overtime.
 *
 * @param sportEventStatus status of event to check for
 * @returns boolean indicating if event is finished after overtime
 */
export function getIsFinishedAfterOvertime(sportEventStatus: EventStatus): boolean {
  switch (sportEventStatus) {
    case 'EndedAfterExtratime':
    case 'EndedAfterPenalties':
    case 'FallbackEndedAfterSuddenDeath':
    case 'FallbackEndedAfterSuperOver':
      return true;
    default:
      return false;
  }
}

/**
 * Get is event finished and not cancelled.
 *
 * @param sportEventStatus status of event to check for
 * @returns boolean indicating if event is finished and not cancelled
 */
export function getIsFinishedNotCancelled(sportEventStatus: EventStatus): boolean {
  return getIsFinished(sportEventStatus) && sportEventStatus !== 'Abandoned' && sportEventStatus !== 'Cancelled';
}

/**
 * Get is event in penalties.
 *
 * @param sportEventStatus status of event to check for
 * @returns boolean indicating if event is in penalties
 */
export function getIsInPenalties(sportEventStatus: EventStatus): boolean {
  switch (sportEventStatus) {
    case 'Penalties':
    case 'EndedAfterPenalties':
      return true;
    default:
      return false;
  }
}

/**
 * Get is event cancelled.
 *
 * @param sportEventStatus status of event to check for
 * @returns boolean indicating if event is cancelled
 */
export function getIsCancelled(sportEventStatus: EventStatus): boolean {
  return sportEventStatus === 'Cancelled';
}

/**
 * Get is event overtime or in penalties.
 *
 * @param sportEventStatus status of event to check for
 * @returns boolean indicating if event is overtime or in penalties
 */
export function getIsOvertimeOrPenalties(sportEventStatus: EventStatus): boolean {
  return getIsOvertime(sportEventStatus) || getIsFinishedAfterOvertime(sportEventStatus);
}

/**
 * Get is event postponed or delayed.
 *
 * @param sportEventStatus status of event to check for
 * @returns boolean indicating if event is postponed or delayed
 */
export function getIsPostponedOrDelayed(sportEventStatus: EventStatus): boolean {
  switch (sportEventStatus) {
    case 'Postponed':
    case 'Delayed':
      return true;
    default:
      return false;
  }
}

/**
 * Get should display red text for status, which is the case for cancelled, postponed or delayed events.
 *
 * @param sportEventStatus status of event to check for
 * @returns boolean indicating if red text should be displayed for the event status
 */
export function getShouldDisplayRedTextForStatus(sportEventStatus: EventStatus): boolean {
  return getIsCancelled(sportEventStatus) || getIsPostponedOrDelayed(sportEventStatus);
}

export type EventStatusInDraw = 'Ongoing' | 'Finished' | 'Cancelled';

/**
 * Get sport event status as 'Ongoing', 'Finished' or 'Cancelled'.
 *
 * @param eventStatus status of event to check for
 * @returns 'Ongoing' | 'Finished' | 'Cancelled' based on event status
 */
export function getEventStatusForDraw(eventStatus: EventStatus): EventStatusInDraw | undefined {
  if (getIsCancelled(eventStatus)) {
    return 'Cancelled';
  }
  if (getIsFinished(eventStatus)) {
    return 'Finished';
  }
  if (getIsOngoing(eventStatus)) {
    return 'Ongoing';
  }
  return undefined;
}
